import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { suppliersApi, CreateSupplierDto } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

interface SupplierFormData {
  name: string;
  code: string;
  contactName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  rating: number;
  isActive: boolean;
}

const getRatingLabel = (rating: number) => {
  if (rating >= 4.5) return 'Excellent';
  if (rating >= 3.5) return 'Good';
  if (rating >= 2.5) return 'Average';
  if (rating >= 1.5) return 'Below Average';
  return 'Poor';
};

export default function SuppliersEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formData, setFormData] = useState<SupplierFormData>({
    name: '',
    code: '',
    contactName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    rating: 3,
    isActive: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    suppliersApi
      .getById(id)
      .then((response) => {
        const supplier = response;
        setFormData({
          name: supplier.name,
          code: supplier.code,
          contactName: supplier.contactName || '',
          email: supplier.email || '',
          phone: supplier.phone || '',
          street: supplier.address?.street || '',
          city: supplier.address?.city || '',
          state: supplier.address?.state || '',
          country: supplier.address?.country || '',
          postalCode: supplier.address?.postalCode || '',
          rating: supplier.rating || 3,
          isActive: supplier.isActive,
        });
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
        navigate('/suppliers');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.code) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const payload: CreateSupplierDto = {
        name: formData.name,
        code: formData.code,
        contactName: formData.contactName || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        rating: formData.rating,
        isActive: formData.isActive,
      };

      if (
        formData.street ||
        formData.city ||
        formData.state ||
        formData.country ||
        formData.postalCode
      ) {
        payload.address = {
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          country: formData.country || undefined,
          postalCode: formData.postalCode || undefined,
        };
      }

      await suppliersApi.update(id!, payload);
      toast.success('Supplier updated successfully');
      navigate(`/suppliers/${id}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Edit Supplier"
        description="Amendments to the recorded details of an existing vendor."
        actions={
          <Button variant="outline" onClick={() => navigate(`/suppliers/${id}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Supplier
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Basic Information</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Acme Corp"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="ACME-001"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active Status</Label>
                <p className="text-sm text-muted-foreground">
                  {formData.isActive ? 'Supplier is active' : 'Supplier is inactive'}
                </p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>
          </div>

          {/* Contact */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Contact</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="contactName">Contact Name</Label>
              <Input
                id="contactName"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@acme.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 555-123-4567"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Address</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="street">Street</Label>
              <Input
                id="street"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Houston"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="TX"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="USA"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="77001"
                />
              </div>
            </div>
          </div>

          {/* Performance Rating */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Performance Rating</h3>
            <Separator />

            <div className="space-y-4">
              <div className="flex justify-between">
                <Label>Rating: {formData.rating}/5</Label>
                <span className="text-sm text-muted-foreground">
                  {getRatingLabel(formData.rating)}
                </span>
              </div>
              <Slider
                value={[formData.rating]}
                onValueChange={(value) => setFormData({ ...formData, rating: value[0] })}
                min={1}
                max={5}
                step={0.5}
                className="w-full"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(`/suppliers/${id}`)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Update Supplier'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
